// Registre central des métadonnées SEO/GEO par page.
//
// Source unique de vérité consommée par :
//  - src/components/LegacyPage.jsx (application des balises <head> côté client)
//  - scripts/prerender.mjs (génération du HTML statique par route)
//  - scripts/generate-sitemap.mjs / scripts/generate-llms.mjs
//
// Les title/description ont été réécrits pour être uniques sur les 20 pages
// (deux doublons existaient auparavant entre des pages produit). Le contenu
// visible des pages n'est jamais
// modifié ici : ce fichier ne pilote que des balises invisibles (<title>, <meta>,
// JSON-LD).
//
// Les tableaux FAQ sont des EXTRACTIONS LITTÉRALES du contenu visible existant
// (sections .faq-item des maquettes), pas de nouveau copywriting.

const DATA_SOURCES_FAQ = [
  {
    q: "D'où vient l'information sur mes contacts ?",
    a: "Tohu Bohu croise 6 sources de données : LinkedIn (signaux publics, changements de poste, publications), Gmail/Outlook (fréquence des échanges et contexte relationnel), votre CRM (historique deals et notes), Crunchbase (financement et signaux entreprise), la presse spécialisée, et l'historique de vos réunions passées. Aucune donnée personnelle sensible n'est traitée sans votre consentement.",
  },
  {
    q: 'Tohu Bohu lit-il le contenu de mes emails ?',
    a: "Non. Tohu Bohu n'accède pas au corps de vos emails. Il analyse uniquement les métadonnées de vos échanges : avec qui vous communiquez, à quelle fréquence, et quand. Cela permet de calculer le score de relation et d'identifier les contacts actifs — sans jamais lire vos messages privés.",
  },
  {
    q: 'Comment Tohu Bohu détecte-t-il les changements de poste ou les signaux LinkedIn ?',
    a: 'Tohu Bohu surveille les données publiques LinkedIn de vos contacts : mises à jour de poste, publications, commentaires, participations à des événements. Ces informations sont publiques et accessibles à tous. Tohu Bohu les capte, les analyse, et vous prévient uniquement quand elles sont pertinentes pour votre relation commerciale.',
  },
  {
    q: 'D’où viennent les signaux sur les entreprises (financement, recrutements) ?',
    a: 'Les signaux entreprise proviennent de sources publiques agrégées : Crunchbase (levées de fonds), LinkedIn Jobs (offres d’emploi), presse tech (TechCrunch, Les Échos, BFM Business), et les communiqués officiels des entreprises. Tohu Bohu ne crée pas de données — il structure et contextualise ce qui existe déjà.',
  },
  {
    q: 'Mes données clients sont-elles partagées entre utilisateurs Tohu Bohu ?',
    a: "Jamais. Vos contacts, vos briefs et votre mémoire relationnelle sont strictement privés et propres à votre compte. En plan Business, seuls les membres de votre équipe que vous avez explicitement invités peuvent accéder à la mémoire partagée. Aucune donnée n'est utilisée pour entraîner des modèles ou partagée avec d'autres clients Tohu Bohu.",
  },
  {
    q: 'Puis-je contrôler quelles sources Tohu Bohu utilise pour moi ?',
    a: "Oui, depuis vos Paramètres > Connecteurs, vous choisissez exactement quelles sources sont actives : Gmail, Outlook, LinkedIn, HubSpot, Salesforce ou Pipedrive. Vous pouvez désactiver n'importe quelle source en 1 clic. Si vous déconnectez une source, les données associées sont supprimées dans les 48h.",
  },
];

const PRICING_FAQ = [
  {
    q: 'Puis-je commencer sans carte bancaire ?',
    a: 'Oui. Le plan Free est gratuit, sans carte bancaire. Il inclut 5 briefs comportementaux par mois, 5 comptes suivis, 25 personnes suivies et Ask Tohu avec quota.',
  },
  {
    q: 'Comment fonctionne la facturation annuelle ?',
    a: 'La facturation annuelle réduit le prix mensuel : Solo passe de 39 € à 29 € par mois, Pro de 59 € à 49 €, et Business de 119 € à 89 €. Les montants de 348 €, 588 € et 1 068 € sont réglés annuellement en une fois.',
  },
  {
    q: 'Peut-on changer de plan à tout moment ?',
    a: 'Oui. Vous pouvez upgrader ou downgrader à tout moment depuis vos Paramètres > Abonnement. Un upgrade prend effet immédiatement, un downgrade prend effet à la fin de la période en cours.',
  },
  {
    q: "Qu'arrive-t-il à ma mémoire Tohu Bohu si je résilie ?",
    a: 'Vos données sont exportables à tout moment depuis Paramètres > Données. En cas de résiliation, elles sont conservées 30 jours, puis supprimées définitivement sur demande ou automatiquement.',
  },
  {
    q: 'Existe-t-il des tarifs pour les grandes équipes ?',
    a: 'Oui. Pour les équipes de plus de 20 commerciaux, nous proposons des tarifs négociés, un déploiement accompagné et une intégration CRM sur mesure. Contactez-nous via le formulaire de démonstration.',
  },
];

const BLOG_AUTHOR = { name: 'Maxime Weinstein', jobTitle: 'CEO & Co-fondateur · Tohu Bohu' };

export const pageMeta = {
  '/': {
    title: 'Tohu Bohu — L’OS Relationnel pour vos équipes B2B',
    description:
      "Tohu Bohu est l'OS Relationnel qui inscrit le capital humain dans la durée. Analysez, performez, transmettez — 500+ signaux comportementaux, 15 frameworks académiques.",
    h1: 'Le système d’exploitation de vos relations B2B. Scorez. Préparez. Protégez.',
    ogType: 'website',
    breadcrumb: [{ name: 'Accueil', path: '/' }],
    schemas: ['organization', 'softwareApplication'],
  },
  '/sante-relationnelle-clients': {
    title: 'Santé Relationnelle — Tohu Bohu',
    description:
      'Tohu Bohu mesure la santé relationnelle de chaque compte client sans sondage — Score 0-100, Verdict actionnable, Portefeuille priorisé par urgence.',
    h1: 'En 30 secondes : vos comptes les plus sains, les plus à risque.',
    ogType: 'website',
    breadcrumb: [{ name: 'Accueil', path: '/' }, { name: 'Santé Relationnelle', path: '/sante-relationnelle-clients' }],
    schemas: ['softwareApplication', 'breadcrumb'],
  },
  '/memoire-relationnelle-client': {
    title: 'Mémoire Relationnelle — Tohu Bohu',
    description:
      "La mémoire relationnelle de vos comptes clients ne doit pas partir avec vos commerciaux. Tohu Bohu capture chaque interaction et sécurise la transmission lors des changements d'affectation.",
    h1: 'La relation appartient à l’entreprise. Pas à ceux qui la quittent.',
    ogType: 'website',
    breadcrumb: [{ name: 'Accueil', path: '/' }, { name: 'Mémoire Relationnelle', path: '/memoire-relationnelle-client' }],
    schemas: ['softwareApplication', 'breadcrumb', 'faq'],
    faq: DATA_SOURCES_FAQ,
  },
  '/brief-relationnel-ia': {
    title: 'Brief Relationnel — Tohu Bohu',
    description:
      'Brief IA en 30 min. Coach discret en live. Mémoire relationnelle permanente. Closez plus avec moins de préparation manuelle.',
    h1: 'Captez chaque signal. Agissez avant tout le monde.',
    ogType: 'website',
    breadcrumb: [{ name: 'Accueil', path: '/' }, { name: 'Brief Relationnel', path: '/brief-relationnel-ia' }],
    schemas: ['softwareApplication', 'breadcrumb', 'faq'],
    faq: DATA_SOURCES_FAQ,
  },
  '/plateforme-intelligence-relationnelle': {
    title: 'Produit — Tohu Bohu | Le Moteur d’Intelligence Relationnelle',
    description:
      'Découvrez le moteur Tohu Bohu : comment 500+ signaux comportementaux et 15 frameworks académiques transforment vos interactions B2B en intelligence relationnelle actionnable.',
    h1: 'Le capital le plus précieux de votre entreprise est aussi le moins bien mesuré.',
    ogType: 'website',
    breadcrumb: [{ name: 'Accueil', path: '/' }, { name: 'Produit', path: '/plateforme-intelligence-relationnelle' }],
    schemas: ['softwareApplication', 'breadcrumb'],
  },
  '/business-development-b2b': {
    title: 'Tohu Bohu for Business Development — Entrez avec un avantage',
    description: "Brief IA en 30s sur chaque cible. Signaux d'entrée temps réel. Capital BD transmissible.",
    h1: 'Vous frappez à de nouvelles portes. Entrez avec un avantage.',
    ogType: 'website',
    breadcrumb: [{ name: 'Accueil', path: '/' }, { name: 'Business Development', path: '/business-development-b2b' }],
    schemas: ['softwareApplication', 'breadcrumb', 'faq'],
    faq: DATA_SOURCES_FAQ,
  },
  '/productivite-reunions': {
    title: 'Tohu Bohu for Productivity — Faites de chaque réunion un actif mesurable',
    description: 'Brief IA avant chaque CODIR, sync, 1:1. Coach en live. Résumé automatique. Mémoire collective.',
    h1: 'Les réunions coûtent cher. Faites-en un actif mesurable.',
    ogType: 'website',
    breadcrumb: [{ name: 'Accueil', path: '/' }, { name: 'Productivité', path: '/productivite-reunions' }],
    schemas: ['softwareApplication', 'breadcrumb', 'faq'],
    faq: DATA_SOURCES_FAQ,
  },
  '/score-relationnel-b2b': {
    title: 'Score Relationnel — Tohu Bohu | Mesurez vos relations B2B',
    description:
      "Le Score Relationnel Tohu Bohu mesure la qualité d'une relation B2B : fréquence, réciprocité, profondeur, longévité, réseau — recalculé après chaque interaction.",
    h1: "La première mesure quantitative de la qualité d'une relation professionnelle.",
    ogType: 'website',
    breadcrumb: [{ name: 'Accueil', path: '/' }, { name: 'Scoring Relationnel', path: '/score-relationnel-b2b' }],
    schemas: ['softwareApplication', 'breadcrumb'],
  },
  '/methodologie-intelligence-relationnelle': {
    title: 'Notre algorithme — Tohu Bohu',
    description:
      "Comment Tohu Bohu transforme 8 sources de données en intelligence relationnelle actionnable. Sources, modèles d'inférence, frameworks académiques.",
    h1: 'Ce que Tohu Bohu sait. Et comment il le sait.',
    ogType: 'website',
    breadcrumb: [{ name: 'Accueil', path: '/' }, { name: 'Méthodologie', path: '/methodologie-intelligence-relationnelle' }],
    schemas: ['softwareApplication', 'breadcrumb'],
  },
  '/integrations-crm-email-calendrier': {
    title: 'Intégrations — Tohu Bohu',
    description:
      'Tohu Bohu se connecte à votre stack existant. Gmail, Outlook, HubSpot, Salesforce, Pipedrive, Slack, LinkedIn, Google Calendar. Zéro reconfiguration.',
    h1: 'S’intègre à votre stack. En 5 minutes.',
    ogType: 'website',
    breadcrumb: [{ name: 'Accueil', path: '/' }, { name: 'Intégrations', path: '/integrations-crm-email-calendrier' }],
    schemas: ['softwareApplication', 'breadcrumb'],
  },
  '/securite-rgpd-donnees': {
    title: 'Sécurité & RGPD — Tohu Bohu',
    description:
      'Vos données ne quittent jamais votre boîte. Hébergement EU, conformité RGPD, chiffrement TLS/AES-256, suppression sur demande sous 48h.',
    h1: 'Vos données ne quittent jamais votre boîte.',
    ogType: 'website',
    breadcrumb: [{ name: 'Accueil', path: '/' }, { name: 'Sécurité & RGPD', path: '/securite-rgpd-donnees' }],
    schemas: ['softwareApplication', 'breadcrumb'],
  },
  '/tarifs': {
    title: 'Tarifs — Tohu Bohu · OS d’Intelligence Relationnelle',
    description:
      'Découvrez les offres Tohu Bohu : Free à 0 €, Solo dès 29 €, Pro dès 49 € et Business dès 89 € par siège et par mois avec facturation annuelle.',
    h1: 'Commencez gratuitement. Scalez quand vous voulez.',
    ogType: 'website',
    breadcrumb: [{ name: 'Accueil', path: '/' }, { name: 'Tarifs', path: '/tarifs' }],
    schemas: ['softwareApplication', 'breadcrumb', 'faq', 'offers'],
    faq: PRICING_FAQ,
    offers: [
      { name: 'Free', price: '0', priceCurrency: 'EUR' },
      { name: 'Solo', price: '29', priceCurrency: 'EUR' },
      { name: 'Pro', price: '49', priceCurrency: 'EUR' },
      { name: 'Business', price: '89', priceCurrency: 'EUR' },
    ],
  },
  '/a-propos-tohu-bohu': {
    title: 'À propos — Tohu Bohu',
    description:
      'Tohu Bohu est construit par des commerciaux, pour des commerciaux. Découvrez Maxime Weinstein et Jordan Chekroun, et la conviction qui a fondé Tohu Bohu.',
    h1: 'Une réunion, c’est d’abord des êtres humains.',
    ogType: 'website',
    breadcrumb: [{ name: 'Accueil', path: '/' }, { name: 'À propos', path: '/a-propos-tohu-bohu' }],
    schemas: ['organization', 'breadcrumb'],
  },
  '/blog': {
    title: 'Blog Tohu Bohu — Sciences comportementales & vente B2B',
    description:
      'Les méthodes comportementales et académiques qui permettent à Tohu Bohu de comprendre vos interlocuteurs. Vulgarisées pour les équipes sales, management et productivité.',
    h1: 'La science derrière l’intelligence relationnelle.',
    ogType: 'website',
    breadcrumb: [{ name: 'Accueil', path: '/' }, { name: 'Blog', path: '/blog' }],
    schemas: ['blogIndex', 'breadcrumb'],
  },
  '/blog/capital-relationnel': {
    title: 'Pourquoi le capital relationnel n’est pas mesuré — Blog Tohu Bohu',
    description:
      'Votre CRM mesure tout sauf l’essentiel. Découvrez pourquoi le capital relationnel est l’actif le plus précieux et le plus invisible de votre entreprise.',
    h1: "Pourquoi le capital relationnel est le seul actif de votre entreprise qui n'est pas mesuré",
    ogType: 'article',
    breadcrumb: [
      { name: 'Accueil', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: 'Capital relationnel', path: '/blog/capital-relationnel' },
    ],
    schemas: ['blogPosting', 'breadcrumb'],
    author: BLOG_AUTHOR,
    readingTime: '8 min',
  },
  '/blog/big-five-vente-b2b': {
    title: 'Le Big Five en vente B2B : comment la personnalité de vos interlocuteurs change tout — Blog Tohu Bohu',
    description:
      'Pourquoi certains contacts décident vite et d’autres attendent — et comment adapter votre approche grâce à la psychologie.',
    h1: 'Le Big Five en vente B2B : comment la personnalité de vos interlocuteurs change tout',
    ogType: 'article',
    breadcrumb: [
      { name: 'Accueil', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: 'Big Five en vente B2B', path: '/blog/big-five-vente-b2b' },
    ],
    schemas: ['blogPosting', 'breadcrumb'],
    author: BLOG_AUTHOR,
    readingTime: '6 min',
  },
  '/blog/granovetter-liens-faibles-reseaux': {
    title: 'La théorie des liens faibles de Granovetter : cartographier l’influence dans un compte — Blog Tohu Bohu',
    description:
      'Le vrai décideur n’est pas toujours celui qui a le plus grand titre. Comprendre les réseaux d’influence change radicalement votre approche.',
    h1: 'La théorie des liens faibles de Granovetter : cartographier l’influence dans un compte',
    ogType: 'article',
    breadcrumb: [
      { name: 'Accueil', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: 'Liens faibles de Granovetter', path: '/blog/granovetter-liens-faibles-reseaux' },
    ],
    schemas: ['blogPosting', 'breadcrumb'],
    author: BLOG_AUTHOR,
    readingTime: '5 min',
  },
  '/blog/kahneman-theorie-perspectives-negociation': {
    title: 'Kahneman, la Prospect Theory et la négociation commerciale — Blog Tohu Bohu',
    description:
      'Nos décisions ne sont pas rationnelles — elles sont prévisibles. Comment utiliser les biais cognitifs pour convaincre plus efficacement.',
    h1: 'Kahneman, la Prospect Theory et la négociation commerciale',
    ogType: 'article',
    breadcrumb: [
      { name: 'Accueil', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: 'Kahneman & Prospect Theory', path: '/blog/kahneman-theorie-perspectives-negociation' },
    ],
    schemas: ['blogPosting', 'breadcrumb'],
    author: BLOG_AUTHOR,
    readingTime: '7 min',
  },
  '/blog/analyse-reseaux-organisationnels-influence': {
    title: 'ONA : comment l’analyse des réseaux organisationnels identifie le vrai décideur — Blog Tohu Bohu',
    description:
      'L’Organizational Network Analysis révèle ce que les organigrammes cachent : qui influence vraiment les décisions d’achat.',
    h1: 'ONA : comment l’analyse des réseaux organisationnels identifie le vrai décideur',
    ogType: 'article',
    breadcrumb: [
      { name: 'Accueil', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: 'ONA & influence organisationnelle', path: '/blog/analyse-reseaux-organisationnels-influence' },
    ],
    schemas: ['blogPosting', 'breadcrumb'],
    author: BLOG_AUTHOR,
    readingTime: '6 min',
  },
  '/blog/theorie-echange-social-reciprocite-b2b': {
    title: 'La théorie de l’échange social : pourquoi la réciprocité est le moteur caché de tout deal B2B — Blog Tohu Bohu',
    description:
      'Blau, Cialdini, et 60 ans de recherche sur un principe simple : dans une relation commerciale, tout ce que vous donnez finit par revenir.',
    h1: 'La théorie de l’échange social : pourquoi la réciprocité est le moteur caché de tout deal B2B',
    ogType: 'article',
    breadcrumb: [
      { name: 'Accueil', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: 'Théorie de l’échange social', path: '/blog/theorie-echange-social-reciprocite-b2b' },
    ],
    schemas: ['blogPosting', 'breadcrumb'],
    author: BLOG_AUTHOR,
    readingTime: '5 min',
  },
};

export function getPageMeta(path) {
  return pageMeta[path] || null;
}

// Liste des articles de blog (path + titre) utilisée pour le schema.org
// CollectionPage/ItemList de la page /blog. Dérivée de src/content/pages.js pour
// rester automatiquement synchronisée si un article est ajouté/retiré.
export function getBlogPosts(pages) {
  return pages
    .filter((page) => page.path.startsWith('/blog/'))
    .map((page) => ({ path: page.path, title: pageMeta[page.path]?.h1 || pageMeta[page.path]?.title || page.path }));
}
